import React, { useState, useEffect } from 'react'
import { updateLessonFile, getLessonContent } from '../services/lessonsApi'
import { toast } from '../utils/toast'
import { formatError } from '../utils/errorHandler'
import './UpdateLessonModal.css'

const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
const INVALID_FILENAME_CHARS = /[/\\:*?"<>|]/g

function UpdateLessonModal({ lesson, onClose, onSuccess }) {
  const [inputMode, setInputMode] = useState('paste')
  const [lessonName, setLessonName] = useState(lesson.lessonName)
  const [fileDisplayName, setFileDisplayName] = useState(lesson.fileDisplayName)
  const [content, setContent] = useState('')
  const [originalContent, setOriginalContent] = useState('')
  const [loadingContent, setLoadingContent] = useState(true)
  const [file, setFile] = useState(null)
  const [updating, setUpdating] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [isAbsorbCourse, setIsAbsorbCourse] = useState(lesson.isAbsorbCourse || false)
  const [absorbCourseId, setAbsorbCourseId] = useState(lesson.isAbsorbCourse ? lesson.lessonKey : '')
  const [passingScore, setPassingScore] = useState(lesson.passingScore ?? 80)
  const [nameAutoPopulated, setNameAutoPopulated] = useState(true)

  const isExistingAbsorb = lesson.isAbsorbCourse || false
  const hasContent = content.trim().length > 0
  const hasFile = file !== null
  const contentChanged = content !== originalContent

  const handleTabSwitch = (mode) => {
    if (mode === inputMode) return
    setInputMode(mode)
    if (mode === 'paste') {
      setFile(null)
      setFileDisplayName(lesson.fileDisplayName)
      const fileInput = document.getElementById('update-file-input')
      if (fileInput) fileInput.value = ''
    }
  }

  const sanitizeFileName = (name) => {
    return name.replace(INVALID_FILENAME_CHARS, '').trim()
  }

  const extractTitleFromMarkdown = (text) => {
    try {
      const frontmatterMatch = text.match(/^---\s*\n([\s\S]*?)\n---\s*\n/)
      if (frontmatterMatch) {
        const frontmatter = frontmatterMatch[1]
        const titleMatch = frontmatter.match(/^title:\s*["']?([^"'\n]+)["']?/m)
        if (titleMatch && titleMatch[1]) {
          return titleMatch[1].trim()
        }
      }

      const h1Match = text.match(/^#\s+(.+)$/m)
      if (h1Match && h1Match[1]) {
        return h1Match[1].trim()
      }

      return null
    } catch (error) {
      console.error('Error extracting title from markdown:', error)
      return null
    }
  }

  const getFileNameWithoutExtension = (filename) => {
    const lastDotIndex = filename.lastIndexOf('.')
    return lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename
  }

  const extractPassingScore = (text) => {
    const requiredScoreMatch = text.match(/required_score:\s*(\d+)/)
    const totalQuestionsMatch = text.match(/total_questions:\s*(\d+)/)

    if (requiredScoreMatch && totalQuestionsMatch) {
      const required = parseInt(requiredScoreMatch[1], 10)
      const total = parseInt(totalQuestionsMatch[1], 10)
      if (total > 0) {
        return Math.round((required / total) * 100)
      }
    }
    return null
  }

  useEffect(() => {
    setLessonName(lesson.lessonName)
    setFileDisplayName(lesson.fileDisplayName)
    setIsAbsorbCourse(lesson.isAbsorbCourse || false)
    setAbsorbCourseId(lesson.isAbsorbCourse ? lesson.lessonKey : '')
    setPassingScore(lesson.passingScore ?? 80)
  }, [lesson])

  useEffect(() => {
    let cancelled = false
    const fetchContent = async () => {
      setLoadingContent(true)
      try {
        const text = await getLessonContent(lesson.lessonKey)
        if (!cancelled) {
          setContent(text)
          setOriginalContent(text)
        }
      } catch (error) {
        console.error('Error fetching lesson content:', error)
        if (!cancelled) {
          setContent('')
          setOriginalContent('')
        }
      } finally {
        if (!cancelled) {
          setLoadingContent(false)
        }
      }
    }
    fetchContent()
    return () => { cancelled = true }
  }, [lesson.lessonKey])

  const handleContentChange = (e) => {
    setContent(e.target.value)
  }

  const handleContentPaste = (e) => {
    setTimeout(() => {
      const pasted = e.target.value
      if (pasted && pasted.trim().length > 0 && (!lessonName.trim() || nameAutoPopulated)) {
        const extractedTitle = extractTitleFromMarkdown(pasted)
        if (extractedTitle) {
          setLessonName(extractedTitle)
          setNameAutoPopulated(true)
        }
      }
    }, 0)
  }

  const handleClearContent = () => {
    setContent('')
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      processFile(selectedFile)
    }
  }

  const processFile = async (selectedFile) => {
    if (!selectedFile.name.toLowerCase().endsWith('.md')) {
      toast.error('Only .md files are allowed')
      return
    }
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5 MB')
      return
    }
    setFile(selectedFile)
    setFileDisplayName(selectedFile.name)

    try {
      const fileContent = await selectedFile.text()
      const extractedTitle = extractTitleFromMarkdown(fileContent)

      if (!lessonName.trim() || nameAutoPopulated) {
        if (extractedTitle) {
          setLessonName(extractedTitle)
        } else {
          const fileNameWithoutExt = getFileNameWithoutExtension(selectedFile.name)
          setLessonName(fileNameWithoutExt)
        }
        setNameAutoPopulated(true)
      }

      const calculatedScore = extractPassingScore(fileContent)
      if (calculatedScore !== null) {
        setPassingScore(calculatedScore)
      }
    } catch (error) {
      console.error('Error reading file:', error)
      if (!lessonName.trim() || nameAutoPopulated) {
        const fileNameWithoutExt = getFileNameWithoutExtension(selectedFile.name)
        setLessonName(fileNameWithoutExt)
        setNameAutoPopulated(true)
      }
    }
  }

  const handleRemoveFile = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setFile(null)
    setFileDisplayName(lesson.fileDisplayName)
    const fileInput = document.getElementById('update-file-input')
    if (fileInput) fileInput.value = ''
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFiles = e.dataTransfer.files
    if (droppedFiles.length > 0) {
      await processFile(droppedFiles[0])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!lessonName.trim()) {
      toast.error('Please enter a lesson name')
      return
    }

    if (isAbsorbCourse && !isExistingAbsorb) {
      if (!absorbCourseId.trim()) {
        toast.error('Please enter an Absorb Course ID')
        return
      }
      if (!GUID_REGEX.test(absorbCourseId.trim())) {
        toast.error('Absorb Course ID must be a valid GUID (e.g., d5779be6-9322-4279-b055-1f5a1602c1cf)')
        return
      }
    }

    const etag = lesson.etag || lesson.ETag
    if (!etag) {
      toast.error('Unable to update: Lesson data is missing. Please refresh the page and try again.')
      return
    }

    setUpdating(true)

    try {
      const sendContent = inputMode === 'paste' && contentChanged && hasContent ? content : null
      const displayName = inputMode === 'upload' && hasFile
        ? fileDisplayName
        : sendContent
          ? `${sanitizeFileName(lessonName) || 'lesson'}.md`
          : fileDisplayName

      await updateLessonFile(
        lesson.lessonKey,
        inputMode === 'upload' ? file : null,
        lessonName,
        etag,
        displayName,
        isAbsorbCourse,
        isExistingAbsorb ? undefined : absorbCourseId.trim(),
        passingScore,
        sendContent
      )
      toast.success('Lesson updated successfully!')

      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      const errorMessage = formatError(error)
      toast.error(errorMessage, 5000)

      if (error.response?.status === 409) {
        setTimeout(() => {
          if (onSuccess) {
            onSuccess()
          }
        }, 1000)
      }
    } finally {
      setUpdating(false)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Update Lesson</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label>Content Source</label>
              <div className="content-tab-group">
                <button
                  type="button"
                  className={`content-tab ${inputMode === 'paste' ? 'active' : ''}`}
                  onClick={() => handleTabSwitch('paste')}
                  disabled={updating}
                >
                  Paste Content
                </button>
                <button
                  type="button"
                  className={`content-tab ${inputMode === 'upload' ? 'active' : ''}`}
                  onClick={() => handleTabSwitch('upload')}
                  disabled={updating}
                >
                  Upload File
                </button>
              </div>
              <span className="content-tab-hint">Switching tabs will clear unsaved content from the other tab</span>
            </div>

            {inputMode === 'paste' && (
              <div className="form-group">
                {loadingContent ? (
                  <div className="content-loading">Loading lesson content...</div>
                ) : (
                  <textarea
                    id="update-content"
                    className="lesson-content-textarea"
                    value={content}
                    onChange={handleContentChange}
                    onPaste={handleContentPaste}
                    placeholder="Paste or type your lesson content here (Markdown supported)"
                    disabled={updating}
                    rows={5}
                  />
                )}
                {hasContent && (
                  <span className="clear-content-link" onClick={handleClearContent}>Clear</span>
                )}
              </div>
            )}

            {inputMode === 'upload' && (
              <div className="form-group">
                <div className="file-upload-wrapper file-upload-compact">
                  <input
                    id="update-file-input"
                    type="file"
                    accept=".md"
                    onChange={handleFileChange}
                    disabled={updating}
                    className="file-input-hidden"
                  />
                  <label
                    htmlFor="update-file-input"
                    className={`file-upload-area ${isDragging ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {file ? (
                      <div className="file-upload-selected">
                        <div className="file-icon">📄</div>
                        <div className="file-details">
                          <div className="file-name">{file.name}</div>
                          <div className="file-size">{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                        </div>
                        <button
                          type="button"
                          className="file-remove-btn"
                          onClick={handleRemoveFile}
                          disabled={updating}
                        >
                          &times;
                        </button>
                      </div>
                    ) : (
                      <div className="file-upload-placeholder">
                        <div className="upload-icon">📤</div>
                        <div className="upload-text">
                          <span className="upload-text-main">Click to upload</span>
                          <span className="upload-text-sub">or drag and drop</span>
                        </div>
                        <div className="upload-hint">Markdown (.md) files only, max 5 MB</div>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="update-lesson-name">Lesson Name *</label>
              <input
                id="update-lesson-name"
                type="text"
                value={lessonName}
                onChange={(e) => { setLessonName(e.target.value); setNameAutoPopulated(false) }}
                placeholder="Enter lesson name"
                disabled={updating}
                required
              />
            </div>

            <div className="form-group">
              <label>Lesson Key</label>
              <code className="lesson-key-display">{lesson.lessonKey}</code>
            </div>

            <div className="form-group absorb-toggle-group">
              <div className="absorb-toggle-row">
                <span className="absorb-toggle-text">Is Absorb Lesson?</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isAbsorbCourse}
                  className={`toggle-switch ${isAbsorbCourse ? 'toggle-on' : ''}`}
                  onClick={() => {
                    if (isExistingAbsorb) return
                    setIsAbsorbCourse(!isAbsorbCourse)
                    if (isAbsorbCourse) setAbsorbCourseId('')
                  }}
                  disabled={updating || isExistingAbsorb}
                >
                  <span className="toggle-knob" />
                </button>
              </div>
              {isAbsorbCourse && (
                <input
                  type="text"
                  value={absorbCourseId}
                  onChange={(e) => setAbsorbCourseId(e.target.value)}
                  placeholder="Absorb Course ID (e.g., d5779be6-9322-4279-b055-1f5a1602c1cf)"
                  disabled={updating || isExistingAbsorb}
                  readOnly={isExistingAbsorb}
                  className={!isExistingAbsorb && absorbCourseId && !GUID_REGEX.test(absorbCourseId.trim()) ? 'invalid-input' : ''}
                />
              )}
              {isAbsorbCourse && (
                <div className="form-group passing-score-group">
                  <label htmlFor="update-passing-score">
                    Passing Score (%)
                    <span className="info-icon" title="The minimum percentage score required to pass this lesson.">
                      i
                    </span>
                  </label>
                  <input
                    id="update-passing-score"
                    type="number"
                    min="0"
                    max="100"
                    value={passingScore}
                    onChange={(e) => setPassingScore(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                    disabled={updating}
                  />
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={updating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="update-btn"
                disabled={updating || !lessonName.trim()}
              >
                {updating ? 'Updating...' : 'Update Lesson'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default UpdateLessonModal
